import type {
  AllHTMLAttributes,
  ForwardedRef,
  ReactNode,
  SyntheticEvent,
} from 'react';
import { forwardRef } from 'react';

import type { Spacing } from '@shieldpay/theme-provider-ui/theme';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { Box } from '../box/box';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';
import { textVariantToStyles } from '../text/text-styles';

import { getInputFieldStyles } from './get-input-field-styles';

const sizeConfig = {
  medium: { paddingY: 'baseNeg2' },
  large: { paddingY: 'base' }, // TODO: base should be basePos1, but I don't want to cause regressions on components that use this.
} satisfies {
  [size: string]: {
    paddingY: Spacing;
  };
};

export type InputFieldSize = keyof typeof sizeConfig;
export interface InputFieldProps
  extends FieldContainerBaseProps,
    Partial<
      Pick<
        AllHTMLAttributes<HTMLInputElement>,
        'placeholder' | 'required' | 'autoComplete'
      >
    > {
  onFocus?: (event: SyntheticEvent) => void;
  onBlur?: () => void;
  onChange?: (event: SyntheticEvent) => void;
  defaultValue?: string | null;
  className?: string;
  extraContainerClasses?: string;
  type?: string; // todo Conform type mismatched 'text' | 'password' | 'tel' | 'search';
  'data-testid'?: string;
  'aria-describedby'?: string;
  children?: ReactNode;
  validating?: boolean;
  size?: InputFieldSize;
  /**
   * Override of logic to show outline error styling on input field without
   * validation messages. e.g. when using this component to compose compound
   * components with multiple inputs and shared validation.
   */
  showVisualErrorStyles?: boolean;
  /**
   * readOnSubmit is a special prop to pass as false on inputs we don’t want to be read as FormData.
   * e.g. the DateInput compound input should not send separate day/month/year
   * This is implemented by not adding the name attr to the input
   */
  readOnSubmit?: boolean;
}

export const TEST_IDS = {
  TEXT_INPUT: 'text-input',
  CONTROL_CONTAINER: 'text-input-control-container',
};

export const InputField = forwardRef(function InputField(
  {
    defaultValue,
    onFocus,
    onBlur,
    onChange,
    extraContainerClasses,
    label,
    screenReaderOnlyLabel = false,
    helpText,
    disabled = false,
    type = 'text',
    'data-testid': testId = TEST_IDS.TEXT_INPUT,
    className,
    children,
    placeholder,
    errors,
    'aria-invalid': invalid,
    'aria-describedby': errorMessageId,
    name,
    showVisualErrorStyles = false,
    required = false,
    readOnSubmit = true,
    autoComplete,
    showField,
    size = 'large', // TODO: Should probably default to medium, but I don't want to cause regressions on components that use this.
    ...inputProps // we want to spread in any other props that are passed in by libraries, e.g. Conform
  }: InputFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { inputResetStyles, stateStyles, boxProps } = getInputFieldStyles({
    hasAdjacentSibling: !!children,
    disabled,
    error: invalid || showVisualErrorStyles,
  });

  return (
    <FieldContainer
      screenReaderOnlyLabel={screenReaderOnlyLabel}
      name={name}
      label={label}
      errors={errors}
      aria-invalid={invalid}
      errorMessageId={errorMessageId}
      disabled={disabled}
      helpText={helpText}
      showField={showField}
    >
      <Box
        stack="row"
        {...boxProps}
        className={classFilter([stateStyles, extraContainerClasses])}
        data-testid={TEST_IDS.CONTROL_CONTAINER}
      >
        <Box<HTMLInputElement>
          {...inputProps}
          Component="input"
          color="currentColor"
          rounded
          expand
          variant="initial"
          className={classFilter([
            inputResetStyles,
            className,
            textVariantToStyles('input'),
          ])}
          id={name}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          padding={{
            y: sizeConfig[size].paddingY,
            left: 'base',
            right: !children ? 'base' : 'none',
          }}
          placeholder={placeholder}
          type={type}
          defaultValue={defaultValue || ''}
          data-testid={testId}
          disabled={disabled}
          name={readOnSubmit ? name : undefined}
          aria-invalid={invalid}
          aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
          // Note this is aria-required to indicate to screen-readers it
          // is a required field. If we used ‘required’ it would block submit
          // if the field is empty, however Blurr handles feedback here.
          aria-required={required}
          ref={ref}
          autoComplete={autoComplete}
        />
        {children}
      </Box>
    </FieldContainer>
  );
});
